import { makeAutoObservable } from 'mobx'
import { toJS } from 'mobx'

import {
  FlightSearchRecommendation,
  FlightSearchesApi,
} from 'shared/api/avia/flight-searches'
import { getApiErrorMessage } from 'shared/lib/errorText'

class FlightSearchesStore {
  list: FlightSearchRecommendation[] | null = null
  isLoading = false
  error: string | null = null
  searchesNeedRefresh = false
  private inFlightRequest: Promise<
    FlightSearchRecommendation[] | null | undefined
  > | null = null
  private invalidationVersion = 0

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  setSearchesNeedRefresh(value: boolean) {
    this.searchesNeedRefresh = value

    if (value) {
      this.invalidationVersion += 1
    }
  }

  setList(data: FlightSearchRecommendation[] | null) {
    this.list = data
  }

  setLoading(loading: boolean) {
    this.isLoading = loading
  }

  setError(error: string | null) {
    this.error = error
  }

  getSearches() {
    return toJS(this.list)
  }

  ensureSearches(): Promise<FlightSearchRecommendation[] | null | undefined> {
    if (this.list !== null && !this.searchesNeedRefresh) {
      return Promise.resolve(toJS(this.list))
    }

    return this.fetchSearches()
  }

  fetchSearches(): Promise<FlightSearchRecommendation[] | null | undefined> {
    if (this.inFlightRequest) return this.inFlightRequest

    this.setLoading(true)
    this.setError(null)

    this.inFlightRequest = this.fetchLatestSearches().finally(() => {
      this.setLoading(false)
      this.inFlightRequest = null
    })

    return this.inFlightRequest
  }

  private async fetchLatestSearches(): Promise<
    FlightSearchRecommendation[] | null | undefined
  > {
    while (true) {
      const requestVersion = this.invalidationVersion

      try {
        const { data } = await FlightSearchesApi.getSearches()

        if (requestVersion !== this.invalidationVersion) continue

        this.setList(data.data)
        this.setSearchesNeedRefresh(false)
        return toJS(this.list)
      } catch (error) {
        if (requestVersion !== this.invalidationVersion) continue

        this.setError(getApiErrorMessage(error) ?? null)
        return undefined
      }
    }
  }
}

export const flightSearchesStore = new FlightSearchesStore()
